import React from 'react'
import { useState } from 'react'

import { isAxiosError } from 'axios'
import { CircleCheck, Link } from 'lucide-react'
import { DateTime } from 'luxon'
import { useAppRouter } from '@/shared/lib/router'
import { toast } from 'sonner'

import { isRedoBlocked } from '../domain/isRedoBlocked'
import { useJourneysStore } from '../stores/useJourneysStore'

import { Level, Stage } from '@/features/journeys'
import { fetchLevelAnswersExport } from '@/features/journeys/services/journey-answer-export.api'
import { generateJourneyAnswersPdf } from '@/features/journeys/services/journey-answer-export.pdf'
import { useCountdown } from '@/shared/hooks/useCountdown'
import { useLevelProgress } from '@/shared/hooks/useLevelProgress'
import { AdminAutoCompleteButton } from '@/shared/ui/AdminAutoCompleteButton'
import { Box } from '@/shared/ui/Box'
import { Button } from '@/shared/ui/Button'
import { CardRewards } from '@/shared/ui/Card'
import { StageText, getIcon } from '@/shared/ui/Icons'
import { Modal, ModalTitle, ModalDescription } from '@/shared/ui/Modal'

interface LevelModalProps {
  level: Level
  stage: Stage
  disabled: boolean
  onHide: () => void
  journeyUid: string
}

export const LevelModal = ({
  level,
  stage,
  disabled,
  onHide,
  journeyUid
}: LevelModalProps) => {
  const Icon = getIcon(level.icon) || StageText
  const router = useAppRouter()

  const lockedUntil = level.locked_until
    ? DateTime.fromISO(level.locked_until, { zone: 'local' })
    : null

  const { isLocked, label } = useCountdown(lockedUntil?.toISO())

  const { levelProgress } = useLevelProgress({ id: level.id })
  const { getLevelByUid } = useJourneysStore()
  const redoBlocked = isRedoBlocked({
    blockRedo: getLevelByUid(level.uid)?.journey.block_redo ?? false,
    levelFinished: levelProgress?.finished ?? false,
    allChallengesFinished: levelProgress?.allChallengesFinished ?? false
  })
  const canOpenLevel = !disabled && !isLocked && !redoBlocked
  const [isDownloading, setIsDownloading] = useState(false)

  const handleClick = () => {
    router.push(`/niveis/${level.uid}`)
    onHide()
  }

  const handleDownloadLevelAnswers = async () => {
    if (!journeyUid || isDownloading) return

    try {
      setIsDownloading(true)
      const data = await fetchLevelAnswersExport(journeyUid, level.uid)
      await generateJourneyAnswersPdf(data)
    } catch (error) {
      if (isAxiosError<{ error?: string }>(error)) {
        toast.error(error.response?.data?.error || 'Nao foi possivel gerar o PDF deste nivel.')
        return
      }

      toast.error('Nao foi possivel gerar o PDF deste nivel.')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Modal className="sm:max-w-xl">
      <Box className="flex flex-col sm:flex-row items-center justify-center gap-2">
        <Icon className="shrink-0" />
        <div className="text-center sm:text-left">
          <ModalTitle className="mb-0 text-md sm:text-lg text-black font-oxygen">
            {stage.name}
          </ModalTitle>
          <ModalDescription className="text-sm sm:text-lg prose text-[#8420B3]">
            {level.name}
          </ModalDescription>
        </div>
      </Box>
      <Button
        variant={levelProgress?.finished ? 'primary' : 'success'}
        disabled={!canOpenLevel}
        className="flex gap-3 min-h-[64px] ps-3 w-full"
        onClick={canOpenLevel ? handleClick : undefined}
      >
        <Link className="w-6 h-6 hidden sm:block" />
        {isLocked ? (
          <div className="flex flex-col text-start">
            <span className="text-base block">Bloqueado</span>
            <span className="text-gray-200 text-sm font-normal block leading-tight">
              Desbloqueia em {label}
            </span>
          </div>
        ) : levelProgress?.finished ? (
          <div className="flex flex-col gap-1 text-start">
            <span className="text-base/5 block">
              {redoBlocked ? 'Concluído' : 'Fazer Jornada Novamente'}
            </span>
            <span className="text-sm font-semibold flex items-start gap-1 text-gray-200">
              <CircleCheck className="size-4 hidden md:block" />
              <span className="block leading-tight ">
                Recompensa Já Coletada
              </span>
            </span>
          </div>
        ) : (
          <div className="flex flex-col text-start">
            <span className="text-base block">Abrir nível</span>
            <span className="text-gray-200 text-sm font-normal items-center gap-1 block leading-tight">
              Recompensa Disponível
            </span>
          </div>
        )}
        <CardRewards
          coinReward={levelProgress?.rewards.totalCoins}
          pointReward={levelProgress?.rewards.totalPoints}
          acquiredPointReward={levelProgress?.rewards.acquiredPoints}
          completed={levelProgress?.finished}
          claimed={levelProgress?.finished}
          className="ml-auto"
          gradient
        />
      </Button>

      {levelProgress?.finished && (
        <Button
          type="button"
          variant="secondary"
          className="w-full"
          loading={isDownloading}
          onClick={handleDownloadLevelAnswers}
        >
          Baixar respostas (PDF)
        </Button>
      )}

      {/* Conclui o nível inteiro pelo pipeline real, para o suporte parar
          exatamente no ponto onde o aluno relatou o problema. */}
      <AdminAutoCompleteButton
        type="Level"
        id={level.id}
        label="Auto-concluir nível"
        className="self-center"
      />
    </Modal>
  )
}
